import { Box, Grid } from '@mui/material';
import { FilterParams } from '@synergy/models';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactElement } from 'react';
import { useIsMobile } from '@synergy/components-next';
import { useNavigate } from 'react-router';

import { ROUTES } from '../../../../../utils/routes';
import { SearchAutocompleteList } from './search-auotcomplete-list';
import { SearchResult, SearchResultItem } from './search-autocomplete-hook';
import { reportSearchInteraction } from '../../../../../store/search-interaction-actions';

export interface SearchAutocompleteContentProps {
    searchResult: SearchResult;
    search?: string;
    onClose: () => void;
}

export const SearchAutocompleteContent = (props: SearchAutocompleteContentProps): ReactElement | null => {
    const { searchResult, search, onClose } = props;
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const intl = useIntl();

    const handleArticleClick = (item: SearchResultItem): void => {
        reportSearchInteraction(searchResult.searchEventId, 'click', item.id, searchResult.articles.indexOf(item) + 1);
        navigate(ROUTES.article.detail.replace(':id', item.id.toString()));
        onClose();
    };

    const handleAllArticleClick = (): void => {
        const searchParams = new URLSearchParams({ [FilterParams.Search]: search ?? '' });
        navigate({ pathname: ROUTES.searchPage, search: searchParams.toString() });
        onClose();
    };

    const handleCategoryClick = (item: SearchResultItem): void => {
        const searchParams = new URLSearchParams({
            [FilterParams.CategoryId]: item.id.toString(),
            [FilterParams.Search]: search ?? '',
        });
        navigate({ pathname: ROUTES.searchPage, search: searchParams.toString() });
        onClose();
    };

    const handleSupplierClick = (item: SearchResultItem): void => {
        const searchParams = new URLSearchParams({
            [FilterParams.SupplierIds]: item.id.toString(),
            [FilterParams.Search]: search ?? '',
        });
        navigate({ pathname: ROUTES.searchPage, search: searchParams.toString() });
        onClose();
    };

    const categoryCount = Math.min(3, searchResult.categories.length ?? 0);
    const supplierCount = Math.min(isMobile ? 3 : 9, searchResult.suppliers.length ?? 0);
    const articleCount = isMobile ? 4 : categoryCount + supplierCount + 1;

    return (
        <Grid container={true}>
            <Grid item={true} xs={12} md={6}>
                <Box sx={{ borderRight: theme => `1px solid ${theme.palette.grey[200]}` }}>
                    <SearchAutocompleteList
                        items={searchResult.articles}
                        title={<FormattedMessage id="app.searchBar.dropdown.articles" />}
                        actionLabel={
                            <FormattedMessage
                                id="app.searchBar.dropdown.allArticles"
                                values={{ count: intl.formatNumber(searchResult.totalArticles) }}
                            />
                        }
                        onItemClick={handleArticleClick}
                        onActionClick={handleAllArticleClick}
                        search={search}
                        count={articleCount}
                    />
                </Box>
            </Grid>
            <Grid item={true} xs={12} md={6}>
                {categoryCount > 0 && (
                    <SearchAutocompleteList
                        items={searchResult.categories}
                        title={<FormattedMessage id="app.searchBar.dropdown.categories" />}
                        onItemClick={handleCategoryClick}
                        count={categoryCount}
                    />
                )}
                <SearchAutocompleteList
                    items={searchResult.suppliers}
                    title={<FormattedMessage id="app.searchBar.dropdown.suppliers" />}
                    onItemClick={handleSupplierClick}
                    count={supplierCount}
                />
            </Grid>
        </Grid>
    );
};
